import { useState } from 'react';
import type { FormEvent } from 'react';
import type {
  NotificationSeverity,
  OpsAuditSortDir,
  QuietNotificationOrg,
} from '@nadoutong/contracts';
import {
  NOTIFICATION_ANNOUNCE_AUDIT_ACTION,
  NOTIFICATION_ANNOUNCE_DESCRIPTION_MAX,
  NOTIFICATION_ANNOUNCE_FINGERPRINT_NAMESPACE,
  NOTIFICATION_ANNOUNCE_SLUG_PATTERN,
  NOTIFICATION_ANNOUNCE_TITLE_MAX,
  NOTIFICATION_QUIET_ORG_DEFAULT_DAYS,
  NOTIFICATION_SEVERITIES,
} from '@nadoutong/contracts';
import { OpsApiError } from '../api/client.ts';
import { useAuditList } from '../api/useAudit.ts';
import { useAnnounceNotification } from '../api/useNotificationAnnouncements.ts';
import {
  useNotificationReach,
  useQuietNotificationOrgs,
} from '../api/useNotificationReach.ts';
import { AnnouncementHistoryRow } from '../components/announcement/AnnouncementHistoryRow.tsx';
import type { ReachState } from '../components/announcement/AnnouncementHistoryRow.tsx';
import { AnnouncementResultPanel } from '../components/announcement/AnnouncementResultPanel.tsx';
import { AnnouncementTestSendDialog } from '../components/announcement/AnnouncementTestSendDialog.tsx';
import type { AnnouncementSendResult } from '../components/announcement/announcementSendResult.ts';
import { formatLocalTimestamp } from '../components/formatters.ts';
import { coerceErrorMessage, useToast } from '../components/Toast.tsx';
import { useT } from '../i18n/index.ts';
import type { I18nKey } from '../i18n/index.ts';
import { generateAnnouncementSlug } from './announcementSlug.ts';
import {
  Button,
  Chip,
  ConfirmDialog,
  FormField,
  Icon,
  IconButton,
  Input,
  PageHeader,
  Pagination,
  Panel,
  Select,
  SortableTh,
  TBody,
  THead,
  Table,
  TableEmpty,
  Td,
  Textarea,
  Th,
  Tr,
} from '../ui/index.ts';

/**
 * `/support/announcements` — write one `kind:"system"` notification to every
 * tenant org (#1396 follow-up). The immediate driver is announcing a newly
 * listed model; the form is copy-only, so maintenance windows and policy
 * changes ride the same surface.
 *
 * THE PAGE'S REAL JOB IS MAKING THE BLAST RADIUS LEGIBLE BEFORE THE CLICK.
 * The write reaches every customer at once and cannot be unsent, and the two
 * failure modes are invisible in the form itself:
 *   - a NEW slug is a permanent event for every targeted org;
 *   - an EXISTING slug silently rewrites whatever that slug said before, and
 *     re-delivers it to every client past its cursor.
 * Hence the type-to-confirm dialog and the `created` / `resurfaced` split in
 * the result panel — `resurfaced > 0` on what the operator believed was a
 * first send means an existing announcement was just rewritten. Since #1965
 * that has a routine cause too (publishing a slug that was test sent first), so
 * the warning names both and asserts neither; softening it to only the routine
 * one would delete the only signal a recycled slug produces.
 *
 * NO AUDIENCE CATEGORY IS SELECTABLE. The form carried a brand scope until
 * #1946; what it filtered on was `orgs.primary_brand`, frozen at signup from the
 * console origin, so picking a brand quietly cut the announcement down to the
 * orgs that had registered on that domain — 9 of 592 on dev, which is how
 * #1945 happened. Brands have collapsed onto nxtpath; a published announcement
 * reaches every tenant org, and there is no control to get wrong.
 *
 * ── THE TEST SEND (#1965), AND WHY IT IS NOT A FIELD ON THIS FORM ───────────
 * An operator needs to see a draft in a real client before the fleet does, which
 * means naming recipients, which means putting a recipient control back on the
 * page that #1945 happened on. It is therefore NOT a field here. The list lives
 * inside `<AnnouncementTestSendDialog />` — mounted only while that dialog is
 * open, unmounted with it — and this component holds no whitelist state of any
 * kind. `submit()` below is the fleet publish; there is no variable it could
 * read a stale list from, so "the whitelist quietly became the default
 * audience" is not a mistake that can be made here, rather than one operators
 * are asked to avoid. The two actions are separate buttons and produce visibly
 * different result panels; only the publish button demands the slug typed back,
 * because only it cannot be taken back.
 *
 * Admin-only at ops-api; a support session gets 403 and never sees the nav
 * entry. Past sends are read back from `ops_audit` rather than from the
 * `notifications` rows: audit keeps every send of a slug including the copy it
 * replaced, while the notifications table only ever holds the latest text.
 *
 * ── THE RECEIVE SIDE (#1947), AND THE WORD THAT MAY NOT APPEAR ─────────────
 * #1946 gave the SEND a record. It did not make the RECEIVE side observable at
 * all, which is what made #1945 take a database query to diagnose. The
 * "polled" column on the history table and the quiet-org panel below it are
 * that half: platform-api records when each org's members last CALLED the
 * notification endpoint, and from that we can say one thing — the org HAD THE
 * CHANCE to see the announcement.
 *
 * It is not read state and not delivery. Nothing on this page may say "read",
 * "delivered" or "received" about it, in any language: an operator who reads
 * "412 read" and acts on it has been told something the system cannot know,
 * which is precisely the shape of the #1945 failure pointed the other way.
 */

const SLUG_FIELD_ID = 'announcement-slug';
const TITLE_FIELD_ID = 'announcement-title';
const DESCRIPTION_FIELD_ID = 'announcement-description';
const SEVERITY_FIELD_ID = 'announcement-severity';

const HISTORY_PAGE_SIZE = 10;
/** Sent at, slug, title, severity, scope, targeted, created, overwritten, polled, operator. */
const HISTORY_COLUMN_COUNT = 10;

export function Announcements(): JSX.Element {
  const t = useT();
  const toast = useToast();
  const announce = useAnnounceNotification();

  const [slug, setSlug] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [severity, setSeverity] = useState<NotificationSeverity>('info');
  const [confirmOpen, setConfirmOpen] = useState(false);
  // Open/closed only. The recipient list itself is state INSIDE the dialog, so
  // it is created on open and destroyed on close — see the header note on why
  // that is the whole defence against #1945 repeating.
  const [testOpen, setTestOpen] = useState(false);
  const [result, setResult] = useState<AnnouncementSendResult | null>(null);
  // Newest first: the question this list answers is "what did we just send",
  // and the answer to it ages out fast.
  const [historyDir, setHistoryDir] = useState<OpsAuditSortDir>('desc');
  const [historyPage, setHistoryPage] = useState(0);

  const history = useAuditList({
    // The same constant platform-api writes: hard-coding it at both ends would
    // let the writer move and leave this filter matching nothing, which renders
    // as "nothing was ever sent" rather than as a break.
    action: NOTIFICATION_ANNOUNCE_AUDIT_ACTION,
    limit: HISTORY_PAGE_SIZE,
    offset: historyPage * HISTORY_PAGE_SIZE,
    dir: historyDir,
  });
  const historyRows = history.data?.audit ?? [];
  const historyTotal = history.data?.total ?? 0;
  const historyPageCount = Math.max(1, Math.ceil(historyTotal / HISTORY_PAGE_SIZE));

  // One batched reach read for the page being rendered, keyed on the audit
  // row's `target_id` (which IS the fingerprint the announce route wrote).
  // Deduplicated because re-firing a slug produces several audit rows sharing
  // one fingerprint, and they all report the same current reach.
  const historyFingerprints = [...new Set(historyRows.map((row) => row.target_id))];
  const reach = useNotificationReach(historyFingerprints);
  const reachByFingerprint = new Map(
    (reach.data?.items ?? []).map((item) => [item.fingerprint, item] as const),
  );
  // Three states, not two. "Did not answer yet" and "answered with an error"
  // both have to stay distinguishable from a number, and from each other.
  const reachState: ReachState = reach.isError
    ? 'error'
    : reach.isPending
      ? 'pending'
      : 'ready';

  const trimmedSlug = slug.trim();
  const trimmedTitle = title.trim();
  const trimmedDescription = description.trim();

  const slugInvalid = trimmedSlug.length > 0 && !NOTIFICATION_ANNOUNCE_SLUG_PATTERN.test(trimmedSlug);
  const titleInvalid = trimmedTitle.length > NOTIFICATION_ANNOUNCE_TITLE_MAX;
  const descriptionInvalid = trimmedDescription.length > NOTIFICATION_ANNOUNCE_DESCRIPTION_MAX;
  const complete =
    NOTIFICATION_ANNOUNCE_SLUG_PATTERN.test(trimmedSlug) &&
    trimmedTitle.length > 0 &&
    !titleInvalid &&
    trimmedDescription.length > 0 &&
    !descriptionInvalid;

  function openConfirm(event: FormEvent): void {
    event.preventDefault();
    if (complete) {
      setConfirmOpen(true);
    }
  }

  function toggleHistoryDir(): void {
    setHistoryDir((current) => (current === 'desc' ? 'asc' : 'desc'));
    // The sort travels to the server, so page 3 of descending has nothing to
    // do with page 3 of ascending.
    setHistoryPage(0);
  }

  /** Shared by both sends: the just-sent row is what the operator wants to see. */
  function showNewestHistoryFirst(): void {
    setHistoryPage(0);
    setHistoryDir('desc');
  }

  // THE FLEET PUBLISH, and it takes no audience argument on purpose: the body
  // is built from the copy fields alone, so there is nothing here that a
  // previous test send could have left behind (#1965).
  function submit(): void {
    announce.mutate(
      {
        slug: trimmedSlug,
        title: trimmedTitle,
        description: trimmedDescription,
        severity,
      },
      {
        onSuccess: (response) => {
          setConfirmOpen(false);
          showNewestHistoryFirst();
          setResult({
            fingerprint: response.fingerprint,
            requestedMode: 'fleet',
            requestedOrgCount: 0,
            mode: response.mode,
            orgsTargeted: response.orgs_targeted,
            created: response.created,
            resurfaced: response.resurfaced,
          });
          toast.success(t('announcements.toast.sent'));
        },
        onError: (err) => {
          setConfirmOpen(false);
          // Same reason the history panel translates its own failure line:
          // `coerceErrorMessage` falls back to hard-coded English, and the
          // server's INTERNAL copy here is a 200-character English sentence
          // that would land verbatim on a console running in Chinese. This is
          // the most consequential message on the page — did 592 orgs just
          // get an unrecallable announcement or not — so it gets a translated
          // string carrying the same hedge and the same remedy. Everything
          // else (validation 400s, auth) still shows the API's own words,
          // which are specific and actionable in a way a generic line is not.
          toast.error(
            err instanceof OpsApiError && err.code === 'INTERNAL'
              ? t('announcements.toast.failed')
              : coerceErrorMessage(err),
          );
        },
      },
    );
  }

  return (
    <div className="flex flex-col gap-4" data-testid="announcements-page">
      <PageHeader
        title={t('announcements.title')}
        sub={t('announcements.subtitle')}
        testId="announcements-header"
      />

      {/* Page-level, not a field hint: it describes who receives the whole
          form's output. Hung off a control it reads as an explanation of that
          control. */}
      <p
        className="m-0 text-dense text-paper-mid"
        data-testid="announcements-audience-note"
      >
        {t('announcements.audience.note')}
      </p>

      <Panel caption={t('announcements.form.caption')} padded>
        <form className="flex flex-col gap-4" onSubmit={openConfirm}>
          <FormField
            label={t('announcements.field.slug')}
            fieldId={SLUG_FIELD_ID}
            required
            hint={t('announcements.field.slug.hint')}
            error={slugInvalid ? t('announcements.field.slug.invalid') : undefined}
          >
            <div className="flex items-center gap-2">
              <Input
                id={SLUG_FIELD_ID}
                mono
                className="flex-1"
                value={slug}
                invalid={slugInvalid}
                aria-describedby={slugInvalid ? `${SLUG_FIELD_ID}-error` : undefined}
                placeholder={t('announcements.field.slug.placeholder')}
                onChange={(event) => setSlug(event.target.value)}
              />
              {/* Generating beats typing here, and not for convenience: a
                  hand-written slug that collides with an old one overwrites
                  that announcement instead of failing. */}
              <IconButton
                label={t('announcements.field.slug.generate')}
                onClick={() => setSlug(generateAnnouncementSlug())}
                data-testid="announcements-slug-generate"
              >
                <Icon name="refresh" size="sm" />
              </IconButton>
            </div>
          </FormField>

          <FormField
            label={t('announcements.field.title')}
            fieldId={TITLE_FIELD_ID}
            required
            hint={t('announcements.field.counter', {
              count: String(trimmedTitle.length),
              max: String(NOTIFICATION_ANNOUNCE_TITLE_MAX),
            })}
            error={titleInvalid ? t('announcements.field.title.too_long') : undefined}
          >
            <Input
              id={TITLE_FIELD_ID}
              value={title}
              invalid={titleInvalid}
              aria-describedby={titleInvalid ? `${TITLE_FIELD_ID}-error` : undefined}
              onChange={(event) => setTitle(event.target.value)}
            />
          </FormField>

          <FormField
            label={t('announcements.field.description')}
            fieldId={DESCRIPTION_FIELD_ID}
            required
            hint={t('announcements.field.counter', {
              count: String(trimmedDescription.length),
              max: String(NOTIFICATION_ANNOUNCE_DESCRIPTION_MAX),
            })}
            error={descriptionInvalid ? t('announcements.field.description.too_long') : undefined}
          >
            <Textarea
              id={DESCRIPTION_FIELD_ID}
              rows={6}
              value={description}
              invalid={descriptionInvalid}
              aria-describedby={
                descriptionInvalid ? `${DESCRIPTION_FIELD_ID}-error` : undefined
              }
              onChange={(event) => setDescription(event.target.value)}
            />
          </FormField>

          <FormField label={t('announcements.field.severity')} fieldId={SEVERITY_FIELD_ID}>
            <Select
              id={SEVERITY_FIELD_ID}
              value={severity}
              onValueChange={(value) => {
                const next = NOTIFICATION_SEVERITIES.find((candidate) => candidate === value);
                if (next !== undefined) setSeverity(next);
              }}
              options={NOTIFICATION_SEVERITIES.map((value) => ({
                value,
                label: t(`announcements.severity.${value}` as I18nKey),
              }))}
            />
          </FormField>

          <div className="flex flex-wrap items-center gap-3">
            <Button type="submit" disabled={!complete || announce.isPending}>
              {announce.isPending
                ? t('announcements.submit.pending')
                : t('announcements.submit')}
            </Button>
            {/* A SEPARATE BUTTON, not a mode toggle on the one above. A toggle
                has a position, and a position can be left where the last
                operator put it — which is #1945 restated. Two buttons cannot
                be misread: whichever one is clicked names its own audience.
                `type="button"` keeps it off the form's submit path. */}
            <Button
              type="button"
              variant="ghost"
              disabled={!complete || announce.isPending}
              onClick={() => setTestOpen(true)}
              data-testid="announcements-test-open"
            >
              {t('announcements.test.open')}
            </Button>
            <p className="m-0 text-label text-paper-low">{t('announcements.submit.warning')}</p>
          </div>
        </form>
      </Panel>

      {result !== null ? <AnnouncementResultPanel result={result} /> : null}

      <Panel caption={t('announcements.history.caption')} testId="announcements-history">
        {history.isError ? (
          // A read failure is NOT an empty history. Falling through to the
          // empty row would tell an admin who just announced something that
          // nothing has ever been sent — the #1945 shape, where the panel
          // states as fact something it has no way to know.
          <div
            role="alert"
            data-testid="announcements-history-error"
            className="mx-4 mt-4 flex flex-col gap-1 rounded-lg border border-bad-line bg-bad-dim/50 px-4 py-3"
          >
            <span className="text-body font-semibold text-bad">
              {t('announcements.history.error')}
            </span>
            <span className="text-dense text-bad-bright">
              {/* Same shape as the ticket queue's error row: the API's own
                  code + message when it answered, and a TRANSLATED fallback
                  when it did not. `coerceErrorMessage` falls back to a
                  hard-coded English string, which on a console running in
                  Chinese surfaces as the one line an operator most needs to
                  read. */}
              {history.error instanceof OpsApiError
                ? `${history.error.code}: ${history.error.message}`
                : t('common.network_error')}
            </span>
          </div>
        ) : null}
        <Table testId="announcements-history-table">
          <THead>
            <Tr interactive={false}>
              <SortableTh
                label={t('announcements.history.column.sent_at')}
                sortKey="created_at"
                sort={{ key: 'created_at', dir: historyDir }}
                onSort={toggleHistoryDir}
                disabled={history.isFetching}
                testId="announcements-history-sort-sent-at"
              />
              <Th>{t('announcements.history.column.slug')}</Th>
              <Th>{t('announcements.history.column.title')}</Th>
              <Th>{t('announcements.history.column.severity')}</Th>
              {/* Which audience each past send actually had (#1965). Without it
                  a test send and a fleet publish differ only by a count, and
                  "3" does not say whether the fleet is three orgs or the send
                  was a draft review. */}
              <Th>{t('announcements.history.column.scope')}</Th>
              <Th num>{t('announcements.history.column.targeted')}</Th>
              <Th num>{t('announcements.history.column.created')}</Th>
              <Th num>{t('announcements.history.column.resurfaced')}</Th>
              {/* Deliberately NOT "read" / "delivered": the number counts orgs
                  whose client called the notification endpoint after this
                  announcement was written. The column header carries the
                  short form and the note under the table carries the full
                  caveat, because a bare ratio in a table is exactly the kind
                  of number that gets quoted without its footnote.

                  The explainer hangs on the HEADER, not on every cell: it
                  describes the column, so repeating it per row was N copies of
                  one fact and put a tooltip about the metric on top of the
                  place a cell's own state (unavailable, unmeasurable) needs to
                  explain itself. */}
              <Th num title={t('announcements.history.column.polled.title')}>
                {t('announcements.history.column.polled')}
              </Th>
              <Th>{t('announcements.history.column.operator')}</Th>
            </Tr>
          </THead>
          <TBody>
            {historyRows.length === 0 ? (
              <TableEmpty colSpan={HISTORY_COLUMN_COUNT} testId="announcements-history-empty">
                {/* Short and DIFFERENT from the alert above it. The banner
                    already carries the full sentence with `role="alert"`; a
                    screen reader that hears it announced and then meets the
                    identical string inside the table is being told the same
                    thing twice by two elements that do not know about each
                    other. This cell only has to keep the table from reading
                    as "nothing has ever been sent". */}
                {history.isError
                  ? t('announcements.history.error.cell')
                  : history.isPending
                    ? t('announcements.history.loading')
                    : t('announcements.history.empty')}
              </TableEmpty>
            ) : (
              historyRows.map((row) => (
                <AnnouncementHistoryRow
                  key={row.id}
                  row={row}
                  reach={reachByFingerprint.get(row.target_id)}
                  reachState={reachState}
                />
              ))
            )}
          </TBody>
        </Table>
        {reach.isError ? (
          // A FAILED READ IS NOT AN EMPTY ONE, and the quiet-org panel below
          // already applies that rule — this panel used to fold the error into
          // the same `—` the loading state renders, so a platform outage looked
          // like a column that never finished loading and said nothing at all.
          // Same discipline, same shape, one page.
          <div
            role="alert"
            data-testid="announcements-reach-error"
            className="mx-4 mt-3 flex flex-col gap-1 rounded-lg border border-bad-line bg-bad-dim/50 px-4 py-3"
          >
            <span className="text-body font-semibold text-bad">
              {t('announcements.history.polled.error')}
            </span>
            <span className="text-dense text-bad-bright">
              {reach.error instanceof OpsApiError
                ? `${reach.error.code}: ${reach.error.message}`
                : t('common.network_error')}
            </span>
          </div>
        ) : null}
        {/* The caveat lives with the table, not only in a tooltip: the column
            is a lower bound on OPPORTUNITY and stating that once, in prose,
            next to the number is the only thing that keeps it from being read
            as a delivery receipt. */}
        <p
          className="mx-4 mt-3 mb-4 text-label text-paper-low"
          data-testid="announcements-history-polled-note"
        >
          {t('announcements.history.polled.note')}
        </p>
        {historyPageCount > 1 ? (
          <Pagination
            page={historyPage + 1}
            pageCount={historyPageCount}
            onPageChange={(page) => setHistoryPage(page - 1)}
            prevLabel={t('common.previous')}
            nextLabel={t('common.next')}
            ariaLabel={t('announcements.history.pagination.aria_label')}
            testId="announcements-history-pagination"
          />
        ) : null}
      </Panel>

      <QuietOrgsPanel />

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={t('announcements.confirm.title')}
        body={t('announcements.confirm.body', {
          fingerprint: `${NOTIFICATION_ANNOUNCE_FINGERPRINT_NAMESPACE}${trimmedSlug}`,
        })}
        confirmLabel={t('announcements.confirm.send')}
        cancelLabel={t('common.cancel')}
        closeLabel={t('common.close')}
        // Typing the slug back is the last checkpoint before an unrecallable
        // fleet-wide write — and it re-reads the exact string that decides
        // whether this creates or overwrites.
        typeToConfirm={trimmedSlug}
        typeToConfirmLabel={t('announcements.confirm.type_slug')}
        loading={announce.isPending}
        onConfirm={submit}
        tone="danger"
        testId="announcements-confirm"
      />

      {/* CONDITIONALLY MOUNTED, not an always-present dialog with an `open`
          prop: unmounting is what discards the recipient list, and discarding
          it is the property that keeps a forgotten whitelist from becoming the
          next publish's audience. A persistent instance would hold that list
          for the rest of the session. */}
      {testOpen ? (
        <AnnouncementTestSendDialog
          draft={{
            slug: trimmedSlug,
            title: trimmedTitle,
            description: trimmedDescription,
            severity,
          }}
          onClose={() => setTestOpen(false)}
          onSent={(sent) => {
            showNewestHistoryFirst();
            setResult(sent);
            // BRANCHED ON WHAT THE SERVER SAID, not on the fact that it
            // answered 200. A platform predating #1965 ignores `test_recipients`
            // and broadcasts, and its response is an ordinary success — an
            // unconditional green "Test sent to 42 orgs — not published to the
            // fleet" would be two false claims raised at the exact moment an
            // unrecallable fleet-wide write landed. The result panel already
            // renders its red alert for that case; a success toast next to it
            // would cancel out the visible half of the only defence there is.
            if (sent.mode === 'whitelist') {
              toast.success(
                t('announcements.test.toast.sent', { count: String(sent.orgsTargeted) }),
              );
            } else {
              toast.error(t('announcements.test.toast.unconfirmed'));
            }
          }}
        />
      ) : null}
    </div>
  );
}

const QUIET_ORGS_PAGE_SIZE = 20;
/** Offered thresholds. Short enough to catch a client that stopped, long
 * enough not to flag a normal holiday. */
const QUIET_ORG_DAY_CHOICES = [7, 14, 30, 90] as const;

/**
 * Orgs where NO member's client has called the notification endpoint inside
 * the chosen window (#1947).
 *
 * It lives on this page, under the send history, because it is the question an
 * operator asks immediately after reading a low reach number: is this
 * announcement's reach low, or was this part of the fleet already silent
 * before it was sent? Separating the two would mean navigating away from the
 * number that prompted the question.
 *
 * "QUIET" IS THE STRONGEST WORD EARNED HERE. A customer whose team is on
 * holiday, one who uses the API without the desktop client, and one whose
 * client is broken all land in this list and are not distinguishable from it.
 * It is a place to start looking, not a fault list — and it is emphatically
 * not a list of people who have not READ anything.
 */
function QuietOrgsPanel(): JSX.Element {
  const t = useT();
  const [days, setDays] = useState<number>(NOTIFICATION_QUIET_ORG_DEFAULT_DAYS);
  const [page, setPage] = useState(0);
  const quiet = useQuietNotificationOrgs({
    days,
    limit: QUIET_ORGS_PAGE_SIZE,
    offset: page * QUIET_ORGS_PAGE_SIZE,
  });
  const rows = quiet.data?.orgs ?? [];
  const total = quiet.data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / QUIET_ORGS_PAGE_SIZE));

  return (
    <Panel caption={t('announcements.quiet.caption')} testId="announcements-quiet">
      <div className="flex flex-col gap-2 px-4 pt-4">
        <p className="m-0 text-dense text-paper-mid">{t('announcements.quiet.note')}</p>
        <div className="flex items-center gap-2">
          <span className="text-label text-paper-mid">{t('announcements.quiet.threshold')}</span>
          <Select
            value={String(days)}
            // The adjacent span is the visible label but is not associated
            // with the control, so a screen reader would otherwise announce a
            // bare combobox reading "14 days".
            ariaLabel={t('announcements.quiet.threshold')}
            testId="announcements-quiet-threshold"
            onValueChange={(value) => {
              const next = QUIET_ORG_DAY_CHOICES.find((choice) => String(choice) === value);
              if (next === undefined) return;
              setDays(next);
              // A different threshold is a different list; page 3 of one has
              // nothing to do with page 3 of the other.
              setPage(0);
            }}
            options={QUIET_ORG_DAY_CHOICES.map((choice) => ({
              value: String(choice),
              label: t('announcements.quiet.threshold.days', { days: String(choice) }),
            }))}
          />
          {quiet.isSuccess ? (
            <Chip tone="neutral">{t('announcements.quiet.total', { total: String(total) })}</Chip>
          ) : null}
        </div>
      </div>
      {quiet.isError ? (
        // Same rule as the send history: a failed read is not an empty list.
        // "No org has gone quiet" is a reassuring claim this panel would have
        // no basis for making.
        <div
          role="alert"
          data-testid="announcements-quiet-error"
          className="mx-4 mt-4 flex flex-col gap-1 rounded-lg border border-bad-line bg-bad-dim/50 px-4 py-3"
        >
          <span className="text-body font-semibold text-bad">{t('announcements.quiet.error')}</span>
          <span className="text-dense text-bad-bright">
            {quiet.error instanceof OpsApiError
              ? `${quiet.error.code}: ${quiet.error.message}`
              : t('common.network_error')}
          </span>
        </div>
      ) : null}
      <Table testId="announcements-quiet-table">
        <THead>
          <Tr interactive={false}>
            <Th>{t('announcements.quiet.column.org')}</Th>
            <Th>{t('announcements.quiet.column.org_id')}</Th>
            <Th num>{t('announcements.quiet.column.members')}</Th>
            <Th>{t('announcements.quiet.column.last_polled')}</Th>
          </Tr>
        </THead>
        <TBody>
          {rows.length === 0 ? (
            <TableEmpty colSpan={4} testId="announcements-quiet-empty">
              {quiet.isError
                ? t('announcements.quiet.error.cell')
                : quiet.isPending
                  ? t('announcements.quiet.loading')
                  : t('announcements.quiet.empty')}
            </TableEmpty>
          ) : (
            rows.map((org) => <QuietOrgRow key={org.org_id} org={org} />)
          )}
        </TBody>
      </Table>
      {pageCount > 1 ? (
        <Pagination
          page={page + 1}
          pageCount={pageCount}
          onPageChange={(next) => setPage(next - 1)}
          prevLabel={t('common.previous')}
          nextLabel={t('common.next')}
          ariaLabel={t('announcements.quiet.pagination.aria_label')}
          testId="announcements-quiet-pagination"
        />
      ) : null}
    </Panel>
  );
}

function QuietOrgRow({ org }: { readonly org: QuietNotificationOrg }): JSX.Element {
  const t = useT();
  return (
    <Tr interactive={false} data-testid={`announcements-quiet-row-${org.org_id}`}>
      <Td emphasis title={org.org_name}>
        {org.org_name}
      </Td>
      <Td mono title={org.org_id}>
        {org.org_id}
      </Td>
      <Td num mono>
        {org.member_count}
      </Td>
      {/* Never-polled reads differently from long-ago-polled and is the more
          interesting of the two: an org with nothing on record may have no
          client installed at all, which no announcement can reach. */}
      <Td mono>
        {org.last_polled_at === null
          ? t('announcements.quiet.never')
          : formatLocalTimestamp(org.last_polled_at, { seconds: true })}
      </Td>
    </Tr>
  );
}
